import type {
	RelationOutputForBuyer,
	RelationOutputForSeller,
} from "@/api/custom_types";
import CheckIcon from "@/assets/icons/Iconex/Light/CheckGreen.svg?react";
import CloseIcon from "@/assets/icons/Iconex/Light/CloseError.svg?react";
import RequestIcon from "@/assets/icons/Iconex/Light/Request.svg?react";
import NiceAvatar from "@/components/base/Avatar";
import { useTranslation } from "@/hooks";
import RelationNameLink from "@/pages/relations/RelationNameLink";
import { useDecideRelation, useGetRelations } from "@/services";
import { currentUserAtom } from "@/store/login";
import {
	Button,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogScrollableArea,
	DialogTitle,
	Typography,
	toast,
} from "@st-marketplace/ui";
import { useAtomValue } from "jotai";
import { memo, useMemo, useState } from "react";
import { usePendingSet } from "../../hooks";

type RelationOutput = RelationOutputForBuyer | RelationOutputForSeller;

type RequestItemProps = {
	req: RelationOutput;
	role: "buyer" | "seller";
	isPending: boolean;
	onAccept?: (id: RelationOutput["id"]) => void;
	onDecline?: (id: RelationOutput["id"]) => void;
};

const RequestItem = memo(function RequestItem({
	req,
	role,
	isPending,
	onAccept,
	onDecline,
}: RequestItemProps) {
	const { t } = useTranslation();

	return (
		<div className="mb-6 flex items-center justify-between">
			<div className="flex items-center gap-3">
				<NiceAvatar
					name={
						req.requester === "BUYER"
							? req.buyer_display_name
							: req.seller_display_name
					}
					width="40px"
					height="40px"
				/>
				<div>
					<Typography variant="body-md">
						<RelationNameLink
							name={
								req.requester === "BUYER"
									? req.buyer_display_name
									: req.seller_display_name
							}
							relationId={req.id}
							party={role}
						/>
					</Typography>
					<Typography variant="body-sm">
						{req.requester === "BUYER"
							? t("relation.requests.wantsToBuyCoins")
							: t("relation.requests.wantsToSellCoins")}
					</Typography>
				</div>
			</div>

			<div className="flex items-center gap-5 overflow-hidden">
				{isPending ? (
					<span className="size-4 animate-spin rounded-full border-2 border-secondary-border border-t-primary-100" />
				) : (
					<>
						<button
							type="button"
							aria-label="Accept request"
							onClick={() => onAccept?.(req.id)}
							className="cursor-pointer text-success-text transition-opacity hover:opacity-70"
						>
							<CheckIcon className="size-6" />
						</button>
						<button
							type="button"
							aria-label="Decline request"
							onClick={() => onDecline?.(req.id)}
							className="cursor-pointer text-danger-text transition-opacity hover:opacity-70"
						>
							<CloseIcon className="size-6" />
						</button>
					</>
				)}
			</div>
		</div>
	);
});

const RelationsPendingRequests: React.FC<{
	relationRole: "buyer" | "seller";
}> = ({ relationRole }) => {
	const { t } = useTranslation();
	const user = useAtomValue(currentUserAtom);

	const decideRelationMutation = useDecideRelation();

	// If the current user is a buyer, we fetch requests where they are the seller
	const { data: requests, isLoading } = useGetRelations({
		query: {
			statuses: ["PENDING"],
			request_receiver_id: user?.id,
			...(relationRole === "buyer"
				? { seller_id: user?.id }
				: { buyer_id: user?.id }),
		},
	});

	const [isOpen, setIsOpen] = useState(false);

	const count = useMemo(() => requests?.count ?? 0, [requests?.count]);

	const { isPending, setItemPending } = usePendingSet<RelationOutput["id"]>();

	const handleAcceptOrRejectAction = (
		id: RelationOutput["id"],
		action: "ACCEPT" | "REJECT",
	) => {
		setItemPending(id, true);
		decideRelationMutation.mutate(
			{
				path: { relation_id: id },
				body: { action: { action_type: "DECIDE", decision: action } },
			},
			{
				onSuccess: async () => {
					toast.success(
						action === "ACCEPT"
							? t("relation.successAcceptAction")
							: t("relation.successRejectAction"),
					);
				},
				onSettled: () => setItemPending(id, false),
			},
		);
	};

	const handleAccept = (id: RelationOutput["id"]) =>
		handleAcceptOrRejectAction(id, "ACCEPT");

	const handleDecline = (id: RelationOutput["id"]) =>
		handleAcceptOrRejectAction(id, "REJECT");

	return (
		<>
			<Button
				type="button"
				color="secondary"
				className="relative normal-case"
				loading={isLoading}
				onClick={() => setIsOpen(true)}
			>
				<RequestIcon className="size-4" />
				{t("relation.requestButton")}
				{count > 0 && (
					<span className="absolute -inset-s-2 -top-2 flex size-5 items-center justify-center rounded-full bg-danger-100 text-[11px] font-semibold text-white">
						{count}
					</span>
				)}
			</Button>

			<Dialog open={isOpen} onOpenChange={setIsOpen}>
				<DialogContent className="w-full max-w-120">
					<DialogHeader>
						<DialogTitle>{t("relation.requests.title")}</DialogTitle>
					</DialogHeader>
					<DialogScrollableArea>
						{count === 0 ? (
							<Typography variant="body-md">
								{t("relation.requests.empty")}
							</Typography>
						) : (
							requests?.results?.map((req: RelationOutput) => (
								<RequestItem
									key={req.id}
									req={req}
									role={relationRole}
									isPending={isPending(req.id)}
									onAccept={handleAccept}
									onDecline={handleDecline}
								/>
							))
						)}
					</DialogScrollableArea>
				</DialogContent>
			</Dialog>
		</>
	);
};

export default RelationsPendingRequests;
